import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { theme } from 'styled-tools';

import DestinationIcon from './DestinationIcon.tsx';
import { StopMarker } from './RouteSegments.tsx';
import RouteStepIcon from './RouteStepIcon.tsx';
import type { Translate } from './types.ts';

export type RouteStepRow = {
  icon?: string;
  /** The icon is a venue POI icon loaded from the CDN, drawn as a circle. */
  cdn?: boolean;
  /** Intermediate stop ordinal; when set, its numbered marker always replaces any `icon`/`cdn` artwork. */
  stop?: number;
  title: string;
  subtitle?: string;
};

export type RouteStepsProps = {
  T: Translate;
  destination: string;
  distance: string;
  duration: string;
  steps: readonly RouteStepRow[];
  /** Pin the header to the nearest scroll container when the whole component scrolls. */
  stickyHeader?: boolean;
  /** Inset the header and row content without moving the instruction-list scrollbar inward. */
  contentInset?: number;
};

// Figma header/row spacing, plus clearance for the instruction-list scrollbar.
const ROUTE_STEPS_SPACING = { headerStart: 10, headerEnd: 7, detail: 4, step: 10, gap: 8, indent: 4 };

const Container = styled.section`
  width: 100%;
  font-family: ${theme('fonts.family')};
  color: ${theme('colors.primaryText')};
`;

const Sentinel = styled.div`
  height: ${theme('borderWidths.sm')};
  margin-block-end: -${theme('borderWidths.sm')};
`;

const Header = styled.header<{ $sticky: boolean; $contentInset: number }>`
  flex: none;
  position: ${props => (props.$sticky ? 'sticky' : 'static')};
  inset-block-start: 0;
  z-index: 1;
  margin-inline: ${props => props.$contentInset}px;
  padding-block: ${ROUTE_STEPS_SPACING.headerStart}px ${ROUTE_STEPS_SPACING.headerEnd}px;
  border-block-end: ${theme('borderWidths.sm')} solid transparent;
  background-color: ${theme('colors.background')};

  &[data-pinned='true'] {
    border-block-end-color: ${theme('colors.secondaryButtonHover')};
  }
`;

// The global `.atrius-map h2`/`p` rules outrank a single class, so `&&` is needed for the Figma typography.
// Figma centres the 26px-leading title in a 21px box; the negative margin keeps that if it wraps.
const Title = styled.h2`
  && {
    font-size: 20px;
    font-weight: ${theme('fontWeights.normal')};
  }

  margin: -2.5px 0;
  line-height: 26px;
  overflow-wrap: anywhere;
`;

const RouteStats = styled.p`
  && {
    font-size: ${theme('fonts.size.h4')};
    font-weight: ${theme('fontWeights.normal')};
  }

  margin: ${ROUTE_STEPS_SPACING.detail}px 0 0;
  color: ${theme('colors.secondaryText')};
  line-height: 21px;
  overflow-wrap: anywhere;
`;

const Steps = styled.ol<{ $contentInset: number }>`
  margin: 0;
  padding: 0;
  padding-inline: ${props => props.$contentInset}px;
  list-style: none;
`;

const Step = styled.li<{ $emphasis: boolean }>`
  display: flex;
  align-items: ${props => (props.$emphasis ? 'center' : 'flex-start')};
  gap: ${ROUTE_STEPS_SPACING.gap}px;
  padding-block: ${ROUTE_STEPS_SPACING.step}px;
  padding-inline-start: ${ROUTE_STEPS_SPACING.indent}px;
  border-block-end: ${theme('borderWidths.sm')} solid ${theme('colors.hoverClickState')};

  &:last-child {
    border-block-end: 0;
  }

  > :first-child {
    flex: none;
  }
`;

const StepText = styled.p`
  flex: 1;
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
`;

const StepTitle = styled.span<{ $emphasis: boolean }>`
  display: block;
  font-size: ${props => theme(props.$emphasis ? 'fonts.size.h3' : 'fonts.size.h4')(props)};
  font-weight: ${theme('fontWeights.normal')};
  line-height: ${props => (props.$emphasis ? 24 : 18)}px;
`;

const StepSubtitle = styled.span<{ $emphasis: boolean }>`
  display: block;
  margin-block-start: ${props => (props.$emphasis ? 0 : ROUTE_STEPS_SPACING.detail)}px;
  color: ${theme('colors.secondaryText')};
  font-size: ${theme('fonts.size.h5')};
  font-weight: ${theme('fontWeights.normal')};
  line-height: 18px;
`;

/** Presentational route steps: each instruction and its metadata share one paragraph and list item. */
export default function RouteSteps({
  T,
  destination,
  distance,
  duration,
  steps,
  stickyHeader = true,
  contentInset = 0,
}: RouteStepsProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isPinned, setIsPinned] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!stickyHeader || !sentinel) {
      return;
    }
    let root = sentinel.parentElement;
    while (root && !/(auto|scroll)/.test(getComputedStyle(root).overflowY)) {
      root = root.parentElement;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setIsPinned(entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0)),
      { root },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [stickyHeader]);

  return (
    <Container aria-label={T('getDirectionsFromTo:Route directions')}>
      <Sentinel ref={sentinelRef} aria-hidden="true" />
      <Header
        $sticky={stickyHeader}
        $contentInset={contentInset}
        data-pinned={stickyHeader && isPinned}
        data-route-directions-header
      >
        <Title>{T('getDirectionsFromTo:To _location_', { location: destination })}</Title>
        <RouteStats>{T('getDirectionsFromTo:_distance_ • _duration_', { distance, duration })}</RouteStats>
      </Header>
      <Steps $contentInset={contentInset} role="list">
        {steps.map((step, index) => {
          // Start, arrival and stop rows have a single prominent line; stops add their ordinal beneath it.
          const emphasis = !step.subtitle || Boolean(step.stop);
          return (
            <Step key={index} $emphasis={emphasis}>
              {step.stop ? (
                <StopMarker aria-hidden="true">{step.stop}</StopMarker>
              ) : step.cdn ? (
                <DestinationIcon name={step.icon} />
              ) : (
                <RouteStepIcon name={step.icon} />
              )}
              <StepText>
                <StepTitle $emphasis={emphasis}>{step.title}</StepTitle>
                {step.subtitle && (
                  <>
                    {' '}
                    <StepSubtitle $emphasis={emphasis}>{step.subtitle}</StepSubtitle>
                  </>
                )}
              </StepText>
            </Step>
          );
        })}
      </Steps>
    </Container>
  );
}
